import { TypedPolicy } from '@/react/portainer/policies/types';
import { useDockerCleanupInitialValues } from '@/react/portainer/policies/components/docker/cleanup/useDockerCleanupInitialValues';

import { FormSection } from '@@/form-components/FormSection';
import { Badge } from '@@/Badge';

import { PolicyTableWrapper } from './PolicyTableWrapper';
import { SettingItem } from './SettingItem';

interface Props {
  policy: TypedPolicy<unknown>;
}

export function DockerCleanupPolicyReadOnly({ policy }: Props) {
  const values = useDockerCleanupInitialValues(policy);

  const nonEmptyExcludedImages = values.excludedImages.filter(Boolean);

  return (
    <div className="space-y-6">
      <FormSection title="Cleanup interval">
        <div className="overflow-hidden rounded-lg border border-gray-5 th-dark:border-gray-7">
          <TextItem label="Cleanup interval" value={values.cleanupInterval} />
        </div>
      </FormSection>

      <FormSection title="Age-based cleanup">
        <div className="overflow-hidden rounded-lg border border-gray-5 th-dark:border-gray-7">
          <SettingItem
            label="Enable age-based cleanup"
            value={values.cleanOldImages}
          />
          {values.cleanOldImages && (
            <TextItem label="Max image age" value={values.imageMaximumGCAge} />
          )}
        </div>
      </FormSection>

      <FormSection title="Storage-limit cleanup">
        <div className="overflow-hidden rounded-lg border border-gray-5 th-dark:border-gray-7">
          <SettingItem
            label="Enable storage-limit cleanup"
            value={values.cleanImagesAtStorageLimit}
          />
          {values.cleanImagesAtStorageLimit && (
            <>
              <TextItem
                label="High threshold (%)"
                value={String(values.highThresholdPercent)}
              />
              <TextItem
                label="Low threshold (%)"
                value={String(values.lowThresholdPercent)}
              />
              <SettingItem
                label="Clear Docker build cache"
                value={values.clearBuildCache}
              />
              <TextItem label="Minimum image age" value={values.minAge} />
              <TextItem
                label="Limit images removed per interval"
                value={
                  values.maxImagesPerInterval === 0
                    ? 'No limit'
                    : String(values.maxImagesPerInterval)
                }
              />
            </>
          )}
        </div>
      </FormSection>

      {nonEmptyExcludedImages.length > 0 && (
        <FormSection title="Excluded images">
          <PolicyTableWrapper
            caption="Images excluded from cleanup"
            data-cy="docker-cleanup-excluded-images-table"
          >
            <thead className="bg-gray-3 th-dark:bg-gray-9">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  Image reference
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-5 th-dark:divide-gray-7">
              {nonEmptyExcludedImages.map((image) => (
                <tr key={image}>
                  <td className="px-4 py-3 text-sm">{image}</td>
                </tr>
              ))}
            </tbody>
          </PolicyTableWrapper>
        </FormSection>
      )}
    </div>
  );
}

function TextItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-gray-5 bg-gray-2 px-4 py-3 last:border-b-0 th-dark:border-gray-7 th-dark:bg-gray-iron-10">
      <span className="text-sm">{label}</span>
      <Badge type="muted">{value}</Badge>
    </div>
  );
}
